/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  ScrollView,
  Animated,
  Keyboard,
} from 'react-native';
import { PanGestureHandler } from 'react-native-gesture-handler';
import Colors from '@global/colors';
import { isWeb, isMobileWeb } from '@global/screens';
import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';
import Modal from '@components/modal/Modal';
import { NoTranslate } from '@components/shared/TranslationContext';
import useMobileWebKeyboardHeight from '@/hooks/useMobileWebKeyboardHeight';

const dimension = Dimensions.get('window');

const ModalHalf = (props) => {
  const { scrollRef = () => {} } = props;
  const [totalHeight, setTotalHeight] = useState(0);
  const [translateY, setTranslateY] = useState(new Animated.Value(0));
  const [showKeyboard, setShowKeyboard] = useState(false);
  const webKeyboardHeight = useMobileWebKeyboardHeight();
  const isKeyboardShown = showKeyboard || webKeyboardHeight > 0;

  const didKeyboardHide = () => setShowKeyboard(false);
  const didKeyboardShow = () => setShowKeyboard(true);

  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener(
      'keyboardDidShow',
      didKeyboardShow,
    );
    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      didKeyboardHide,
    );

    return () => {
      keyboardDidShowListener.remove();
      keyboardDidHideListener.remove();
    };
  }, []);

  const onClose = () => {
    try {
      props.onClose();
    } catch (error) {}
  };

  const onPanGestureEvent = (e) => {
    if (!isWeb) {
      const scroll = e?.nativeEvent?.translationY;
      if (scroll >= 0) {
        setTranslateY(scroll);
      }
    }
  };

  const onHandleGestureEvent = (e) => {
    if (!isWeb) {
      const scroll = e?.nativeEvent?.translationY;
      if (scroll >= 0) {
        if (scroll > (totalHeight ? totalHeight / 1.3 : 100)) {
          setTranslateY(new Animated.Value(0));
          onClose();
        } else {
          setTranslateY(new Animated.Value(0));
        }
      }
    }
  };

  // Modal renders its children via a Portal that re-parents them onto a
  // detached PortalHost elsewhere in the tree, so a Context provider
  // wrapping <Modal> itself never reaches the portaled content. It has to
  // wrap the JSX passed *into* Modal instead.
  const innerContent = (
    <PanGestureHandler
      onGestureEvent={onPanGestureEvent}
      onHandlerStateChange={onHandleGestureEvent}>
      <Animated.View
        style={[
          styles.modalHalf,
          isWeb && !isMobileWeb
            ? {
                borderRadius: 5,
                paddingBottom: props.removePaddingBottom ? 0 : 10,
              }
            : {
                borderTopLeftRadius: 15,
                borderTopRightRadius: 15,
                paddingBottom: props.compactPaddingBottom ? 10 : 50,
              },
          { transform: [{ translateY: translateY }] },
          webKeyboardHeight
            ? {
                bottom: webKeyboardHeight,
                maxHeight: window.visualViewport.height - 20,
                paddingBottom: 10,
              }
            : {},
        ]}
        onLayout={(e) => {
          setTotalHeight(e?.nativeEvent?.layout?.height);
        }}>
        <View
          style={[
            styles.modalHeader,
            isWeb && props.headerBordered
              ? {
                  borderBottomColor: Colors.grey0,
                  borderBottomWidth: 1,
                }
              : {},
          ]}>
          <SociollaText
            testID={`${props.testID}-title`}
            type="medium"
            style={[
              { fontSize: 15, letterSpacing: 0.5, width: 260 },
              props.titleStyle ? props.titleStyle : {},
            ]}
            ellipsizeMode="tail"
            numberOfLines={1}>
            {props.title}
          </SociollaText>
          <TouchableOpacity onPress={() => onClose()}>
            <Image
              testID={`${props.testID}-close-icon`}
              width={16}
              height={16}
              style={{ width: 16, height: 16 }}
              svg={true}
              source={require('@assets/img/icons/close-grey.svg')}
            />
          </TouchableOpacity>
        </View>
        {props.searchInput ? props.searchInput : false}
        <ScrollView
          testID={`${props.testID}-scrollview`}
          ref={scrollRef}
          style={[
            { backgroundColor: Colors.white },
            isWeb ? { overflow: 'auto' } : {},
            isKeyboardShown ? { maxHeight: 220 } : {},
          ]}>
          {props.children}
        </ScrollView>
      </Animated.View>
    </PanGestureHandler>
  );

  return (
    <Modal
      testID={`${props.testID}-modal`}
      transparent={true}
      visible={props.visible}
      animationType={isWeb ? 'fade' : 'slide'}
      maxWidth={props.maxWidth ? props.maxWidth : '100%'}
      width={props.width ? props.width : '100%'}
      spaceFromTop={props.spaceFromTop}
      spaceFromLeft={props.spaceFromLeft}
      centered={props.centered}
      closeOutside={props.closeOutside}
      onClose={() => onClose()}>
      {props.noTranslate ? (
        <NoTranslate>{innerContent}</NoTranslate>
      ) : (
        innerContent
      )}
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalHalf: {
    width: '100%',
    maxHeight: isMobileWeb
      ? 'calc(100vh - 100px)'
      : isWeb
      ? 'calc(100vh - 200px)'
      : dimension.height / 1.2,
    backgroundColor: Colors.white,
    paddingBottom: isWeb && !isMobileWeb ? 0 : 50,
    position: isMobileWeb ? 'fixed' : isWeb ? 'relative' : 'absolute',
    bottom: 0,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: isWeb ? 15 : 20,
    paddingBottom: 15,
    paddingHorizontal: 20,
  },
});

export default React.memo(ModalHalf, (prev, next) => prev == next);
